import type { EditorInstance } from "@quire/editor";
import { useEffect, useState } from "preact/hooks";
import type { App } from "../app.ts";
import { Actions } from "./actions.tsx";
import { Bar } from "./bar.tsx";
import { Details } from "./details.tsx";
import { useAppState } from "./hooks.ts";
import { IconButton } from "./icon.tsx";
import { Modes } from "./modes.tsx";
import type { NewMode } from "./new-form.tsx";
import { OutlineRail, useOutlineTracking } from "./outline.tsx";
import { Palette } from "./palette.tsx";
import { Preview } from "./preview.tsx";
import { Sheet } from "./sheet.tsx";
import { Sidebar } from "./sidebar.tsx";

export function Root({ app }: { app: App }) {
  const s = useAppState(app);
  const [newMode, setNewMode] = useState<NewMode>(null);
  const [editor, setEditor] = useState<EditorInstance | null>(null);
  useOutlineTracking(app);

  useEffect(() => {
    document.body.dataset.sidebar = s.sidebarOpen ? "open" : "closed";
  }, [s.sidebarOpen]);
  // Focus mode: the editor fades what is not being written, and the controls step aside
  // while typing. Moving the pointer or pressing Escape brings them back.
  useEffect(() => {
    editor?.setFocus?.({ focus: s.focus, typewriter: s.typewriter });
  }, [editor, s.focus, s.typewriter]);
  useEffect(() => {
    const body = document.body;
    body.dataset.focus = s.focus ? "on" : "off";
    body.dataset.typewriter = s.focus && s.typewriter ? "on" : "off";
    delete body.dataset.quiet;
    if (!s.focus) return;
    const hush = (e: KeyboardEvent) => {
      if (e.key === "Escape") delete body.dataset.quiet;
      else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) body.dataset.quiet = "on";
    };
    const wake = () => delete body.dataset.quiet;
    addEventListener("keydown", hush);
    addEventListener("pointermove", wake);
    addEventListener("pointerdown", wake);
    return () => {
      removeEventListener("keydown", hush);
      removeEventListener("pointermove", wake);
      removeEventListener("pointerdown", wake);
    };
  }, [s.focus, s.typewriter]);
  useEffect(() => {
    document.title = s.current ? `${s.current.title || s.current.path} · Quire` : "Quire";
  }, [s.current?.title, s.current?.path]);

  const begin = (mode: Exclude<NewMode, null>) => {
    setNewMode(mode);
    app.toggleSidebar(true);
  };

  return (
    <>
      <div class="island top sidebar-island">
        <IconButton
          icon="sidebar"
          label={s.sidebarOpen ? "Hide files" : "Show files"}
          tip="Files and folders (Ctrl+\\)"
          class="sidebar-toggle"
          aria-controls="sidebar"
          aria-expanded={s.sidebarOpen}
          on={false}
          onClick={() => app.toggleSidebar()}
        />
      </div>
      <Modes app={app} />
      <Actions app={app} canFocus={Boolean(editor?.setFocus)} />
      {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: click-away backdrop; Escape and the toggle button are the keyboard routes */}
      <div class="scrim" hidden={!s.sidebarOpen} onClick={() => app.toggleSidebar(false)} />
      <Sidebar app={app} newMode={newMode} setNewMode={setNewMode} />
      <Sheet app={app} onEditor={setEditor} begin={begin} />
      <Details app={app} open={s.detailsOpen} />
      <section id="export" class="popover narrow" aria-label="Export" hidden={!s.exportOpen}>
        <button class="button block" type="button" onClick={() => app.exportCurrent()}>
          {s.noteHasImages
            ? "Download this note with its pictures (.zip)"
            : "Download this file (.md)"}
        </button>
        <button class="button block" type="button" onClick={() => app.exportAll()}>
          Download everything (.zip)
        </button>
      </section>
      <OutlineRail app={app} />
      <Preview app={app} />
      <Bar app={app} editor={editor} />
      <Palette app={app} begin={begin} canFocus={Boolean(editor?.setFocus)} />
      {s.notice && (
        <div class="notice" role="alertdialog" aria-label="Notice" data-tone={s.notice.tone}>
          <p class="notice-text">{s.notice.text}</p>
          <div class="row">
            {s.notice.actions.map((a, i) => (
              <button
                key={a.key}
                class={i === 0 ? "button primary" : "button"}
                type="button"
                ref={i === 0 ? (el) => el?.focus() : undefined}
                onClick={() => app.answerNotice(a.key)}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
